import * as React from "react";
import clsx from "clsx";
import { Typography, theme } from "antd";
import type { SlideEditTypeId } from "@/state/slideEditStore";

const { Title } = Typography;

const CHART_TIME_PERIOD_ICON = "assets/slides/slideIcons/chartTimePeriod.png";
const CHART_RELATIVE_BENCHMARK_ICON = "assets/slides/slideIcons/chartRelativeBenchmark.png";
const CHART_CUSTOMIZATION_ICON = "assets/slides/slideIcons/chartCustomization.png";
const TEXT_ON_SLIDE_ICON = "assets/slides/slideIcons/textOnSlide.png";

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <polyline
      points="2.5,7 6,10.5 11.5,3.5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SLIDE_EDIT_TYPE_OPTIONS = [
  {
    id: "chart-time-period-price-refresh" as const,
    label: "Chart Time Period (Price Refresh)",
    icon: CHART_TIME_PERIOD_ICON,
  },
  {
    id: "chart-relative-benchmarks" as const,
    label: "Chart Relative Benchmarks",
    icon: CHART_RELATIVE_BENCHMARK_ICON,
  },
  {
    id: "chart-customization-options" as const,
    label: "Chart Customization Options",
    icon: CHART_CUSTOMIZATION_ICON,
  },
  {
    id: "text-on-slide" as const,
    label: "Text on Slide",
    icon: TEXT_ON_SLIDE_ICON,
  },
] as const;

const EditTypeIcon = ({ icon, alt }: { icon: string; alt: string }) => (
  <img
    src={icon}
    alt={alt}
    width={56}
    height={56}
    className="mb-2 h-24 w-24 shrink-0 object-contain"
    loading="lazy"
  />
);

type EditTypeOptionCardProps = {
  label: string;
  icon: string;
  selected: boolean;
  onSelect: () => void;
};

function EditTypeOptionCard({ label, icon, selected, onSelect }: EditTypeOptionCardProps) {
  const { token } = theme.useToken();
  const buttonToken: any = token?.Button;
  const primaryColor = buttonToken?.colorPrimary;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onSelect}
      className={clsx(
        "relative flex min-w-0 flex-1 flex-col items-start gap-3 rounded-xl border-[1.5px] px-4 pb-4 pt-5 text-left outline-none transition-[border-color,background-color] duration-150 ease-out",
        selected ? "bg-green-50" : "border-gray-200 bg-white",
        !selected && "cursor-pointer hover:border-gray-300",
        selected && "cursor-pointer"
      )}
      style={selected ? { borderColor: primaryColor } : undefined}
    >
      <div className="absolute right-3.5 top-3.5">
        {selected ? (
          <div
            className="flex h-6 w-6 items-center justify-center rounded-full"
            style={{ backgroundColor: primaryColor }}
          >
            <CheckIcon />
          </div>
        ) : (
          <div className="h-6 w-6 rounded-full border-[1.5px] border-gray-300 bg-white" />
        )}
      </div>

      <div className="flex w-full items-start gap-5">
        <div className="relative flex w-[120px] shrink-0 items-center justify-center">
          {selected ? (
            <span
              className="absolute -top-2 left-0 inline-block w-[120px] rounded-md px-[9px] py-0.5 text-center text-[11px] font-semibold leading-[18px] tracking-wide text-white"
              style={{ backgroundColor: primaryColor }}
            >
              Selected
            </span>
          ) : null}
          <EditTypeIcon icon={icon} alt={label} />
        </div>
        <div className="min-w-0 flex-1 self-center">
          <div className="text-[15px] font-semibold leading-snug text-gray-900">{label}</div>
        </div>
      </div>
    </button>
  );
}

type SlideEditTypeRadioListProps = {
  value: SlideEditTypeId[];
  onToggle: (id: SlideEditTypeId) => void;
};

export default function SlideEditTypeRadioList({ value, onToggle }: SlideEditTypeRadioListProps) {
  const selectedSet = React.useMemo(() => new Set(value), [value]);

  return (
    <div className="box-border h-full min-h-0 overflow-y-auto p-6 text-gray-800">
      <Title level={4} className="!mb-6 text-gray-900">
        Edit Type
      </Title>
      <div
        className="grid grid-cols-1 gap-4 md:grid-cols-2"
        role="group"
        aria-label="Slide edit types"
      >
        {SLIDE_EDIT_TYPE_OPTIONS.map((opt) => {
          const selected = selectedSet.has(opt.id);
          return (
            <EditTypeOptionCard
              key={opt.id}
              label={opt.label}
              icon={opt.icon}
              selected={selected}
              onSelect={() => onToggle(opt.id)}
            />
          );
        })}
      </div>
    </div>
  );
}
